import React from "react"
import { PageDrawerAction } from "src/components/ui/PageDrawerAction"
import { Icons } from "src/components/ui/Icons"
import { useActions, useAppState, useReaction } from "src/state"
import { 
    UserFragment,
    Users_Insert_Input,
    UpdateUserMutationVariables,
    User_Roles_Enum,
    User_Type_Enum,
    User_Status_Enum
} from "src/state/gql/_types"
import { FormDrawer } from "src/components/forms/FormDrawer"
import { Loader } from "@mantine/core"
import { 
    AgencyOperationsAdminVenueUserCreateFragment, 
    AgencyOperationsAdminVenueUserCreateSchema, 
    AgencyOperationsAdminVenueUserUpdateFragment, 
    AgencyOperationsAdminVenueUserUpdateSchema 
} from "src/components/agency/operations/administrator/venues/users/AgencyOperationsAdminVenueUserSchemas"
import { extractCurrentItemData } from "src/utils/forms"
import { uuidToVibrantHexColor } from "src/utils/calendar"

type FormPayloadType = AgencyOperationsAdminVenueUserCreateFragment

let initialState: FormPayloadType = {
    role: User_Roles_Enum.VenueOperationsAdministrator,
    type: User_Type_Enum.Operations,
    email: '',
    first_name: '',
    last_name: '',
    phone: '',  
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    color: uuidToVibrantHexColor(crypto.randomUUID()),
    timezone: 'America/New_York'
};

export const CreateAgencyOperationsAdminVenueUserDrawer: React.FC<{onClose():void}> = ({ onClose }) => {

    const { createItem, updateItem, deleteItem } = useActions().users;
    const { loading, errors } = useAppState().users;
    const { currentItemId: currentTenantId } = useAppState().tenants;
    
    let currentItemId = null;
    let currentItemData: FormPayloadType | null = null;

    const handleCreate = (payload: Users_Insert_Input) => {
        return createItem({ tenant_id: currentTenantId, ...payload })
    }

    return (
        <>
            <FormDrawer<FormPayloadType, UserFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminVenueUserCreateSchema}
                createAction={handleCreate}
                updateAction={updateItem}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialState}
                currentItemData={currentItemData}
                onClose={onClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const CreateAgencyOperationsAdminVenueUserAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Add User'
            action={CreateAgencyOperationsAdminVenueUserDrawer}
            icon={Icons.add}
        />
    )
}

let initialStateUpdate: AgencyOperationsAdminVenueUserUpdateFragment = {
    status: User_Status_Enum.Active,
    role: User_Roles_Enum.Administrator,
    type: User_Type_Enum.Operations,
    email: '',
    first_name: '',
    last_name: '',
    phone: '',  
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    color: "",
    timezone: 'America/New_York'
};

export const UpdateAgencyOperationsAdminVenueUserDrawer: React.FC<{onClose():void,deselect:boolean}> = ({ onClose, deselect = true }) => {

    
    const { updateItem, deleteItem, deselectItem } = useActions().users;
    const { currentItem, loading, errors } = useAppState().users;

    if (loading || !currentItem) return <Loader />

    let currentItemId = null;
    let currentItemData: AgencyOperationsAdminVenueUserUpdateFragment | null = null;
    if (currentItem) {
        currentItemId = currentItem.id;
        currentItemData = extractCurrentItemData<AgencyOperationsAdminVenueUserUpdateFragment>(initialStateUpdate, currentItem);
    }

    const handleClose = () => {
        if(deselect) {
            deselectItem()
        }
        onClose()
    }

    const handleUpdate = (payload: UpdateUserMutationVariables) => {
        return updateItem({
            id: payload.id,
            _set: {
                ...payload._set
            }
        })
    }

    return (
        <>
            <FormDrawer<AgencyOperationsAdminVenueUserUpdateFragment, UserFragment, string>
                autoOpen={false}
                formSchema={AgencyOperationsAdminVenueUserUpdateSchema}
                updateAction={handleUpdate}
                deleteAction={deleteItem}
                isLoading={loading}
                errors={errors}
                currentItemId={currentItemId}
                initialData={initialStateUpdate}
                currentItemData={currentItemData}
                onClose={handleClose}
                deleteRedirect={true}
            />

        </>
    )

}

export const UpdateAgencyOperationsAdminVenueUserAction = () => {

    const reaction = useReaction()
    const { deselectItem } = useActions().users;
    const [opened, setOpened] = React.useState(false);

    React.useEffect(() => reaction(
        (state) => state.users.currentItemId,
        (selectedID) => {
            setOpened(!!selectedID)
        }, { immediate: false }
    ))

    const handleClose = () => {
        deselectItem()
    }

    return (
        <PageDrawerAction<UserFragment>
            title='Edit User'
            action={UpdateAgencyOperationsAdminVenueUserDrawer}
            icon={Icons.edit}
            startOpened={opened}
            onClose={handleClose}
            hidden
        />
    )
}

export const EditAgencyOperationsAdminVenueUserAction = () => {
    return (
        <PageDrawerAction<UserFragment>
            title='Edit User'
            action={UpdateAgencyOperationsAdminVenueUserDrawer}
            icon={Icons.edit}
            props={{
                deselect: false
            }}
        />
    )
}